import { supabase } from './supabase';
import { EXPORT_CHUNK, EXPORT_HARD_CAP } from './ledgerExport';

// Issue #194, Session 78n10. The vendor catalog's CSV export.
//
// In plain terms: Jennifer can add vendors one at a time and nothing else.
// This gives her a file of every vendor — names, contacts, addresses, payment
// terms — that she can open in Excel, and that Stede can read back in. The
// export and the import are deliberately the SAME file, which is the whole
// design decision this module is built around; see THE ROUND TRIP below.
//
// Approved gate: docs/gates/Stede_DocumentOutput_Gate_395.html, decisions D5,
// D9 and D12. #194 is an application of conventions those decisions already
// settled rather than a new set — which is exactly why it was scheduled last.
//
// This module owns fetching and shaping only. Anything display-related is
// passed in, matching ledgerExport.js, so the page imports this and never the
// reverse.

// ── THE ROUND TRIP ───────────────────────────────────────────────────────────
//
// One column contract, used by BOTH directions. The export writes these
// headers in this order; the import reads them by header name, so a person may
// reorder or delete columns in Excel and the file still loads.
//
// `Stede ID` is what makes this an UPDATE format rather than a duplicate
// factory. A row carrying an id updates that vendor; a row with the cell blank
// is a new vendor. Without it the only key is the name, and editing a vendor's
// name in the spreadsheet would silently create a second one — which is #211's
// defect class arriving in bulk.
//
// Deliberately NOT exported: created_at, reviewed_at, reviewed_by and
// similarity_warning_note. They are records of what Stede did, not fields a
// person maintains, and a column that is editable-looking but ignored on
// import is worse than an absent one.
export const VENDOR_COLUMNS = [
  { header: 'Stede ID', key: 'id', kind: 'key' },
  { header: 'Vendor name', key: 'name', kind: 'required' },
  { header: 'Contact name', key: 'contact_name' },
  { header: 'Email', key: 'email' },
  { header: 'Phone', key: 'phone' },
  { header: 'Street address', key: 'street_address' },
  { header: 'Address line 2', key: 'address_line2' },
  { header: 'City', key: 'city' },
  { header: 'State', key: 'state' },
  { header: 'ZIP', key: 'postal_code' },
  { header: 'Payment terms', key: 'payment_terms' },
  { header: 'QuickBooks name', key: 'quickbooks_vendor_name' },
  { header: 'Notes', key: 'notes' },
  { header: 'Internal', key: 'is_internal', kind: 'bool' },
  { header: 'Internal branch', key: 'branch_id', kind: 'branch' },
  { header: 'Active', key: 'is_active', kind: 'bool' },
];

// The columns the page's own list query does not select. Stated here because
// the omission is easy to mistake for a bug: VendorCatalogPage loads what the
// cards and the detail panel render, and the seven address and terms columns
// added at 78n8 (#403) are read by the purchase order rather than by the list.
// The export therefore runs its OWN query rather than shaping page state, which
// is the same reason ledgerExport.js does.

function statusFilterToQuery(query, statusFilter) {
  if (statusFilter === 'Active') return query.eq('is_active', true);
  if (statusFilter === 'Inactive') return query.eq('is_active', false);
  return query; // 'All'
}

// How many vendors the active filter matches, WITHOUT fetching them. Gate D5:
// state the number before the file is made. Stated before, it is a promise;
// printed on the finished file, it only ever describes whatever arrived.
export async function countVendorsForExport({ statusFilter }) {
  let query = supabase
    .from('inventory_vendors')
    .select('id', { count: 'exact', head: true });
  query = statusFilterToQuery(query, statusFilter);
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

// Fetch every matching vendor, paging until the set is complete.
//
// The vendor catalog is small today — dozens of rows — so this will page once
// and stop. It pages anyway, and self-calibrates the same way the ledger's
// export does, because the failure it prevents is invisible when it happens:
// PostgREST's own max-rows setting caps an unranged select silently, producing
// a well-formed, complete-looking, incomplete file. That is #394 item 1, and a
// table being small today is not a reason to write the version that breaks
// when it is not.
export async function fetchAllVendorsForExport(opts, knownTotal) {
  const total = knownTotal != null ? knownTotal : await countVendorsForExport(opts);

  const rows = [];
  let offset = 0;
  let step = EXPORT_CHUNK;

  while (rows.length < total && rows.length < EXPORT_HARD_CAP) {
    let query = supabase
      .from('inventory_vendors')
      .select(`
        id, name, contact_name, email, phone,
        street_address, address_line2, city, state, postal_code,
        payment_terms, quickbooks_vendor_name,
        notes, is_internal, branch_id, is_active
      `)
      .order('name', { ascending: true })
      .range(offset, offset + step - 1);
    query = statusFilterToQuery(query, opts.statusFilter);

    const { data, error } = await query;
    if (error) throw error;

    const batch = data || [];
    if (batch.length === 0) break;
    rows.push(...batch);
    if (batch.length < step) step = batch.length;
    offset += batch.length;
  }

  return rows;
}

// The search box on the Vendor Catalog is applied in the browser, so the export
// applies it the same way and over the WHOLE set rather than over whatever page
// happened to be on screen.
export function applyVendorSearch(rows, search) {
  if (!search) return rows;
  const q = search.toLowerCase();
  return rows.filter(v =>
    v.name?.toLowerCase().includes(q) ||
    v.contact_name?.toLowerCase().includes(q) ||
    v.email?.toLowerCase().includes(q) ||
    v.phone?.toLowerCase().includes(q)
  );
}

// Yes/No rather than true/false. The file is read and edited by a person in
// Excel before it comes back, and the importer accepts both spellings plus 1/0
// — the export picks the one that reads as English.
function boolCell(v) {
  return v ? 'Yes' : 'No';
}

export function buildVendorTable(rows, { branchName }) {
  const headers = VENDOR_COLUMNS.map(c => c.header);
  const body = rows.map(v => VENDOR_COLUMNS.map(c => {
    if (c.kind === 'bool') return boolCell(v[c.key]);
    // The branch is written as its NAME, not its id. An id is meaningless in a
    // spreadsheet and unmaintainable by hand; the importer resolves the name
    // back. Blank for every ordinary vendor, which is all but a handful --
    // branch_id is only meaningful when Internal is Yes.
    if (c.kind === 'branch') return v[c.key] == null ? '' : branchName(v[c.key]);
    return v[c.key] ?? '';
  }));
  return { headers, body };
}

// ── The self-describing header block ─────────────────────────────────────────
// Gate D12. A file that cannot describe itself cannot be relied on as a record.
//
// This block carries one thing the ledger's does not, and it is the reason the
// round trip works: a statement that the file IS the import format. The
// importer skips these rows by finding the row whose first cell is the first
// column header, so provenance and re-importability are not in tension — the
// file describes itself AND loads back in.
//
// SAP and Coupa both hand you a bare headers-and-rows extract with no
// provenance, and NetSuite's carries a title but is not its own import format.
// Neither gives you a file you can read six months later and also feed back.
export function describeVendorExport({
  companyName, statusLabel, search, rowCount, generatedBy, generatedAt,
}) {
  const lines = [
    [companyName],
    ['Vendor catalog'],
    [],
    ['Vendors included', statusLabel],
    ['Search', search || '(none)'],
    ['Rows in this file', String(rowCount)],
    ['Generated by', generatedBy],
    ['Generated', generatedAt],
    [],
    ['This file is also Stede’s import format.',
      'Edit it and import it back from the Vendor Catalog. Rows keep their Stede ID; clear that cell to add a new vendor.'],
    ['Before editing in Excel',
      'Format the ZIP and Phone columns as Text. Excel drops a leading zero from 01234 and will write 1234 back.'],
    [],
  ];
  return lines;
}

// ── The audit trail ──────────────────────────────────────────────────────────
// Gate D9, and the same best-effort contract as everywhere else: written after
// the file is produced, so a failed export is not recorded as one that
// happened, and a log that cannot be written never costs somebody their file.
//
// `layout` IS NULL AND THAT IS DELIBERATE, NOT AN OVERSIGHT. It is a closed
// CHECK of the four layout CHOICES the ledger and the invoice documents offer.
// This export has no choice inside it — there is one vendor catalog and one
// shape for it — so inventing a value to satisfy a column would be recording a
// decision nobody made. That is decision C of
// db/migrations/20260830_quickbooks_connection_and_gl_mapping.sql, which made
// the column nullable for exactly this case: a QuickBooks push has no layout
// either. NO MIGRATION IS NEEDED HERE, and a session tempted to add a 'catalog'
// value should read that migration's header first.
export async function logVendorExport({ profile, filters, rowCount }) {
  try {
    await supabase.from('export_log').insert({
      exported_by: profile?.id || null,
      exported_by_name_snapshot: profile?.full_name || 'Unknown',
      surface: 'vendor_catalog',
      layout: null,
      format: 'csv',
      filters,
      row_count: rowCount,
      // No cost column exists on a vendor record, so this is false as a
      // statement of fact rather than as a permission outcome.
      included_cost: false,
    });
  } catch {
    /* best-effort, deliberately */
  }
}
